import { useAuth } from '@/hooks/use-auth/use-auth';
import { useLogout } from '@/hooks/use-logout/use-logout';
import { useUser } from '@/hooks/use-user/use-user';
import { CallTakerLayout } from '@/layouts/call-taker-layout/call-taker-layout';
import DefaultLayout from '@/layouts/default-layout/default.layout';
import { httpClient } from '@/services/request/request.service';
import { Spin } from 'antd';
import { AxiosError, AxiosResponse, HttpStatusCode } from 'axios';
import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

type PrivateRouteProps = {
  children: React.ReactNode;
};

export const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const { logout } = useLogout();
  const { isAuthorized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, isLoading } = useUser();

  const isCallTaker = userData?.roles.some((role) => role.name === 'call_taker');

  useEffect(() => {
    const interceptor = httpClient.interceptors.response.use(
      (res: AxiosResponse) => res,
      (err: AxiosError) => {
        if (err?.response?.status === HttpStatusCode.Unauthorized) {
          logout();
          navigate('/sign-in', { state: { from: location.pathname } });
        }
        if (err?.response?.status === HttpStatusCode.NotFound) {
          navigate('/404', { replace: true });
        }
        return err;
      }
    );

    return () => httpClient.interceptors.response.eject(interceptor);
  }, [logout, navigate, location.pathname]);

  if (!isAuthorized) {
    return <Navigate to="/sign-in" state={{ from: location?.pathname ?? '/' }} replace />;
  }

  if (isLoading || !userData) {
    return (
      <Spin
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      />
    );
  }

  if (isCallTaker) {
    return <CallTakerLayout>{children}</CallTakerLayout>;
  }

  return <DefaultLayout>{children}</DefaultLayout>;
};
